/**
 * Document Matching Quality (DM pack — DM2).
 *
 * Content-aware relevance scoring for the doc-vs-checklist-item match.
 *
 * The legacy matcher in `src/config/documentChecklists.ts` is a
 * tag-pattern substring sweep — it has no concept of "does this doc
 * actually contain the evidence the checklist item asks for?". The
 * Redhead Feasibility regression (a Purchase Option Agreement listed
 * as Primary evidence for Geotechnical + Utility + Title + Zoning +
 * Flood simultaneously) is the consequence: a doc with passing
 * mentions of "land", "easement", "utility" tags satisfies every
 * pattern.
 *
 * This module replaces tag matching with a small Gemini call that
 * reads the doc text + the requirement and returns a 0..1 score, a
 * ≤200-char rationale, and ≤3 short evidence quotes. The output is
 * cached in Firestore keyed on `(docId, itemId, contentHash,
 * MODEL_VERSION)` so we don't re-score the same doc-item pair every
 * rebuild — re-scoring is only triggered when the doc bytes change
 * (contentHash) or the scoring prompt itself bumps (MODEL_VERSION).
 *
 * The configured Argon or 3.x call has a 45-second budget (20s for
 * older/unknown models), outside the existing throttle queue
 * so a hung Gemini call doesn't park rebuild. On timeout / error /
 * empty doc the result is a 0-score row with an explanatory rationale
 * — callers get a typed score, except deliberate cancellation rejects.
 *
 * Only verdicts on the document's content are cached. A timeout, an API
 * error, or an empty body (text not extracted / not hydrated yet — the
 * key's `contentHash` is the upload bytes, so the key does not change
 * when the text lands) returns the same 0-score row to the caller but is
 * NOT written, so the next call re-scores. The filename-negation 0 is a
 * deterministic verdict and IS cached. Rows that pre-fix builds cached
 * for those non-verdicts (score 0 with the timeout / error rationale at
 * confidence 'low', or the exact no-text rationale) are read as cache
 * MISSES and overwritten by the next successful score — this heals them
 * without a MODEL_VERSION bump, which would invalidate every good row.
 *
 * Cache layout (Firestore):
 *
 *   docMatching/{cacheKey}
 *     cacheKey = `${docId}::${itemId}::${contentHash}::${MODEL_VERSION}`
 *     {
 *       score: number,
 *       rationale: string,
 *       evidenceQuotes: string[],
 *       confidence: 'low' | 'medium' | 'high',
 *       modelVersion: string,
 *       docId: string,
 *       itemId: string,
 *       contentHash: string,
 *       scoredAt: string,        // ISO timestamp of the score write
 *       siteId?: string,         // best-effort context for the admin panel
 *       // TEL-04 write provenance (rows written before it lack all three):
 *       scoringModel: string,    // Gemini model that scored, or 'filename-gate'
 *       writerOrigin: string,    // window.location.origin, or 'server'
 *       buildSha: string,        // short SHA of the writing build, or 'unknown'
 *     }
 *
 * Burst control: in the browser every fresh call is a `/api/gemini-proxy`
 * request against the proxy's per-IP bucket (100 / 60 s). Each one first
 * takes a slot from `scoringThrottle` (4 in flight, 60 starts per sliding
 * 60 s, none before the proxy's `Retry-After`); a cache hit never waits, and
 * the wait is outside the per-call timeout budget. A rate-limited call is not
 * re-fired inside `runGeminiJSON`: it opens the back-off window and is tried
 * once more after it. Server runtimes call Vertex directly and are not
 * throttled.
 *
 * A timeout / error row carries `transientFailure`, so a caller that keeps
 * results of its own (the site coverage session cache) can tell "the call
 * failed, try again later" from "no match".
 * The provenance fields are write-only: they are not part of the cache key
 * (no invalidation, no MODEL_VERSION bump), `isLegacyNonVerdictRow` ignores
 * them, and `readCachedScore` strips them so a cached `RelevanceScore` has
 * the same shape as a fresh one. They exist so a census of cached rows can
 * attribute each row to the tab, preview or server and the build and model
 * that wrote it, instead of presuming origin from site and time.

 *
 * Telemetry: cache hits / misses are tracked in module-scope counters
 * exposed via `getScoringTelemetry()` so the admin DM7 panel + tests
 * can verify the cache layer is doing what it claims. An ignored legacy
 * row counts as a miss.
 */
import type { SiteDocument } from '../../types/siteRag';
import { documentBodyText } from '../../lib/documentBody';
import { documentContentNamesRequirement } from '../knowledge/documentTopics';
import type { ChecklistItem } from '../../config/documentChecklists';
import { withTimeout, isTimeoutError } from '../../lib/withTimeout';
import { raceWithAbort, rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';
import { filenameStronglyNegates } from '../../lib/docMatchFilenameGate';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import {
  acquireScoringSlot,
  isRateLimitedError,
  isScoringThrottleEnabled,
  noteScoringRateLimited,
  noteScoringRetryRequest,
} from './scoringThrottle';
import { getBuildSha } from '../../lib/runtimeFlags';


/**
 * Bump this constant whenever the scoring prompt below changes. The
 * cache key includes MODEL_VERSION so callers automatically miss-and-
 * re-score on a prompt change.
 *
 * Versioning convention: `YYYYMMDD-N` where N is the daily sequence.
 */
// 20260907-1 (P3) — the scorer reads the segment of the body that the content
// index says speaks to the requirement, not only the first 8,000 chars.
export const MODEL_VERSION = '20260907-1';

/** Default per-call timeout budget in milliseconds. */
export function scoringTimeoutMsForModel(model: string): number {
  return /^gemini-3(?:[.-])/i.test(model) || /^gemini-4-argon$/i.test(model)
    ? 45_000 : 20_000;
}
export const DEFAULT_SCORING_TIMEOUT_MS = scoringTimeoutMsForModel(GEMINI_PRO_MODEL);

/** Maximum chars of doc text sent to the scorer — keeps prompt cost bounded. */
export const MAX_DOC_CHARS_FOR_SCORING = 8_000;

/**
 * Score thresholds and the SED D0.3 extraction floor live in the
 * dependency-free `lib/docMatching/relevanceThresholds` (single source of
 * truth, referenced by DM3 + DM4); re-exported here so every existing
 * importer of this module is unchanged.
 */
export {
  PRIMARY_SCORE_THRESHOLD,
  SUPPORTING_SCORE_THRESHOLD,
  EXTRACTION_RELEVANCE_FLOOR,
} from '../../lib/docMatching/relevanceThresholds';

export type RelevanceConfidence = 'low' | 'medium' | 'high';

/** Why the model call produced no verdict: it timed out, or it failed. */
export type TransientScoringFailure = 'timeout' | 'error';

/**
 * Result of scoring one (doc, item) pair. Returned unless cancelled — even on
 * timeout / failure the caller gets a 0-score row with an explanatory
 * rationale rather than a thrown error.
 */
export interface RelevanceScore {
  /** 0..1 — strict per the prompt below. */
  score: number;
  /** ≤200 chars naming the specific evidence (or its absence). */
  rationale: string;
  /** Up to 3 short verbatim quotes lifted from the doc. */
  evidenceQuotes: string[];
  /** Model self-reported confidence in its own score. */
  confidence: RelevanceConfidence;
  /** MODEL_VERSION that produced this score. */
  modelVersion: string;
  /** True when this row came back from the cache (i.e. no fresh Gemini call). */
  cached?: boolean;
  /**
   * Set when the model call timed out or failed (a rate limit included): the
   * 0 is not a verdict on the content, it is not cached, and a later call
   * with the same inputs may succeed. Absent on every verdict, and on the
   * empty-body 0 — that one is not cached either, but only a change to the
   * document's text (which moves every key that reads it) changes it.
   */
  transientFailure?: TransientScoringFailure;
}

export interface ScoreDocOptions {
  /** Optional — surfaces in the cache row + the AdminPanel DM7 leaderboard. */
  siteId?: string;
  /** Override the per-call timeout budget (test seam). */
  timeoutMs?: number;
  /** When set, skips the Firestore cache read (test seam / forced refresh). */
  skipCache?: boolean;
  /**
   * Cancels queued and active model work with the caller's reason. An aborted
   * call is not a no-match verdict. Cancellation before the content verdict
   * prevents its cache write; a completed verdict already being persisted may
   * finish, but cannot be published to the cancelled caller.
   */
  signal?: AbortSignal;
}

/* ── Telemetry counters ───────────────────────────────────────────── */

let cacheHits = 0;
let cacheMisses = 0;
let timeouts = 0;
let errors = 0;
let filenameShortCircuits = 0;

export interface ScoringTelemetry {
  cacheHits: number;
  cacheMisses: number;
  timeouts: number;
  errors: number;
  /** DMS-4 — count of `(doc, item)` pairs short-circuited to score 0
   *  by `filenameStronglyNegates` BEFORE any Gemini call fires. */
  filenameShortCircuits: number;
}

/** Read the in-process telemetry counters. Used by tests + DM7. */
export function getScoringTelemetry(): ScoringTelemetry {
  return { cacheHits, cacheMisses, timeouts, errors, filenameShortCircuits };
}

/** Reset counters — exported for tests. */
export function resetScoringTelemetry(): void {
  cacheHits = 0;
  cacheMisses = 0;
  timeouts = 0;
  errors = 0;
  filenameShortCircuits = 0;
}

/* ── Cache key ────────────────────────────────────────────────────── */

/**
 * Stable cache key for a (doc, item, contentHash, modelVersion) tuple.
 * Exported so callers (DM7 admin panel) can compose the same key when
 * inspecting cache rows.
 */
export function cacheKeyFor(args: {
  docId: string;
  itemId: string;
  contentHash: string;
  modelVersion?: string;
}): string {
  return `${args.docId}::${args.itemId}::${args.contentHash}::${args.modelVersion ?? MODEL_VERSION}`;
}

/* ── Lazy dependencies ──────────────────────────────────────────── */

/**
 * One shared import per lazy dependency: concurrent scores (a matcher
 * fan-out) wait on the same promise instead of each starting an import. A
 * failed import is forgotten, so the next call tries again. The imports stay
 * dynamic so callers that never score do not load Firestore or the Gemini
 * client.
 */
function lazyImport<T>(load: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | undefined;
  return () => {
    pending ??= load().catch((e: unknown) => {
      pending = undefined;
      throw e;
    });
    return pending;
  };
}

const loadFirebase = lazyImport(() => import('../../firebase'));
const loadFirestore = lazyImport(() => import('firebase/firestore'));
const loadGeminiClient = lazyImport(() => import('../geminiClient'));

/* ── Firestore I/O ────────────────────────────────────────────────── */

const DOC_MATCHING_COLLECTION = 'docMatching';

/* ── Non-verdict rationales ───────────────────────────────────────── */

// The rationales `callGeminiForScore` writes when it has no verdict on
// the content. The writer below and `isLegacyNonVerdictRow` share these
// so the heal predicate cannot drift from what pre-fix builds cached.
// Changing a string here stops rows carrying the OLD string from being
// healed.
/** Timeout rationale prefix; the budget in ms and a fixed suffix follow. */
const TIMEOUT_RATIONALE_PREFIX = 'Scoring call exceeded ';
/** Error rationale prefix; the error message and a fixed suffix follow. */
const ERROR_RATIONALE_PREFIX = 'Scoring failed (';
/** Exact empty-body rationale (written at confidence 'high'). */
const NO_TEXT_RATIONALE = 'No extracted text on the document — cannot evaluate evidence.';

/**
 * True for a cached row that a pre-fix build wrote for a non-verdict:
 * a timeout or API error (score 0, confidence 'low', rationale starting
 * with the timeout / error prefix) or an empty body (score 0 with the
 * exact no-text rationale, any confidence). Such rows are read as misses.
 * A 0 with any other rationale or confidence — including the filename-
 * negation 0 — is a real verdict and stays a hit.
 */
function isLegacyNonVerdictRow(data: Partial<RelevanceScore>): boolean {
  if (data.score !== 0 || typeof data.rationale !== 'string') return false;
  if (data.rationale === NO_TEXT_RATIONALE) return true;
  return data.confidence === 'low'
    && (data.rationale.startsWith(TIMEOUT_RATIONALE_PREFIX)
      || data.rationale.startsWith(ERROR_RATIONALE_PREFIX));
}

/* ── Write provenance (TEL-04) ────────────────────────────────────── */

/** `scoringModel` of a verdict the filename gate reached without an LLM call. */
export const FILENAME_GATE_SCORER = 'filename-gate';

/** Provenance stamped on every docMatching row this module writes. */
export interface DocMatchingWriteProvenance {
  /** Model id that produced the verdict, or FILENAME_GATE_SCORER. */
  scoringModel: string;
  /** The writing page's `window.location.origin` (production host vs a
   *  localhost / preview tab), or `'server'` outside a browser. */
  writerOrigin: string;
  /** Short SHA of the writing build (`__BUILD_HASH__`, else the server's
   *  BUILD_SHA / BUILD_HASH env), or `'unknown'`. */
  buildSha: string;
}

const PROVENANCE_KEYS: ReadonlyArray<keyof DocMatchingWriteProvenance> = ['scoringModel', 'writerOrigin', 'buildSha'];

/** Where this row is being written from — see DocMatchingWriteProvenance.writerOrigin. */
export function docMatchingWriterOrigin(): string {
  try {
    const origin = typeof window !== 'undefined' ? window.location?.origin : undefined;
    if (typeof origin === 'string' && origin.length > 0 && origin !== 'null') return origin;
  } catch { /* no usable location (sandboxed / non-browser) */ }
  return 'server';
}

/** Build SHA of the writer — see DocMatchingWriteProvenance.buildSha. */
export function docMatchingWriterBuildSha(): string {
  const sha = getBuildSha();
  if (sha !== 'unknown') return sha;
  // The server bundle (esbuild) has no __BUILD_HASH__ define; Cloud Run gets
  // the SHA through the environment instead (see server.ts PROXY_VERSION).
  const env = typeof process !== 'undefined' ? process.env : undefined;
  return env?.BUILD_SHA || env?.BUILD_HASH || 'unknown';
}

/**
 * Read a previously-cached score from Firestore. Returns null when no
 * cache row exists, the row is a legacy non-verdict
 * (`isLegacyNonVerdictRow`), or the read fails. Never throws — a
 * Firestore outage degrades to "cache miss", not "scoring blocked".
 * The TEL-04 write provenance is dropped, so a hit has the same shape as
 * a fresh score.
 */
async function readCachedScore(key: string): Promise<RelevanceScore | null> {
  try {
    const { db } = await loadFirebase();
    const { doc, getDoc } = await loadFirestore();
    const snap = await getDoc(doc(db, DOC_MATCHING_COLLECTION, key));
    if (!snap.exists()) return null;
    const data = { ...(snap.data() as RelevanceScore & Partial<DocMatchingWriteProvenance>) };
    if (isLegacyNonVerdictRow(data)) return null;
    for (const k of PROVENANCE_KEYS) delete data[k];
    return { ...data, cached: true };
  } catch (e) {
    // Cache-read failure is silent — we just fall through to a fresh
    // score. Surface in debug so a recurring outage shows up in
    // dev-tools without polluting the user-facing console.
    console.debug('[doc-match] cache read failed:', (e as Error)?.message);
    return null;
  }
}

/**
 * Persist a freshly-computed score. Soft-fail: a Firestore write
 * outage degrades to "we just don't cache this run", not "scoring
 * blocked". Strips undefined / non-finite numbers so Firestore
 * accepts the row.
 */
async function writeCachedScore(
  key: string,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    const { db } = await loadFirebase();
    const { doc, setDoc } = await loadFirestore();
    const clean = JSON.parse(JSON.stringify(payload, (_, v) => {
      if (typeof v === 'number' && !Number.isFinite(v)) return null;
      if (v === undefined) return null;
      return v;
    }));
    await setDoc(doc(db, DOC_MATCHING_COLLECTION, key), clean);
  } catch (e) {
    console.debug('[doc-match] cache write failed:', (e as Error)?.message);
  }
}

/* ── Gemini call ──────────────────────────────────────────────────── */

const SCORING_SCHEMA = {
  type: 'object',
  required: ['score', 'rationale', 'evidenceQuotes', 'confidence'],
  properties: {
    score: { type: 'number' },
    rationale: { type: 'string' },
    evidenceQuotes: { type: 'array', items: { type: 'string' } },
    confidence: { type: 'string', enum: ['low', 'medium', 'high'] },
  },
} as const;

function buildScoringPrompt(doc: SiteDocument, item: ChecklistItem): string {
  // P3 — when the content index found a segment for this requirement, score
  // THAT passage (wherever it sits in the body) instead of the opening pages.
  const body = documentBodyText(doc);
  const segment = (doc.topicSegments ?? []).find(s => s.requirementIds.includes(item.id));
  const docText = segment
    ? body.slice(segment.charStart, segment.charEnd).slice(0, MAX_DOC_CHARS_FOR_SCORING)
    : body.slice(0, MAX_DOC_CHARS_FOR_SCORING);
  const segmentNote = segment
    ? `Content index: segment "${segment.topic}" (chars ${segment.charStart}-${segment.charEnd}) — ${segment.summary}`
    : (doc.topics?.topics?.length ? `Content index topics: ${doc.topics.topics.join('; ')}` : '');
  return [
    'You evaluate whether a document satisfies a specific evidence',
    'requirement for a site feasibility report. Be strict. A document',
    'that only mentions the requirement in passing is NOT a match.',
    '',
    `Requirement: "${item.label}"`,
    `Description: "${item.description}"`,
    '',
    `Document filename: "${doc.fileName ?? '(unknown)'}"`,
    ...(segmentNote ? [segmentNote] : []),
    segment ? 'Document text (the indexed segment, up to 8000 chars):' : 'Document text (first 8000 chars):',
    docText.length > 0 ? docText : '(no extracted text — score 0)',
    '',
    'Return JSON: { score: 0..1, rationale: "≤200 chars naming the',
    'specific evidence", evidenceQuotes: ["≤3 short quotes from the',
    'doc"], confidence: "low|medium|high" }',
    '',
    'Strict scoring:',
    '- 0.9+   doc IS the requirement (e.g. a borehole log file for a',
    '         geotechnical report)',
    '- 0.7-0.89 doc contains the substantive data the requirement demands',
    '- 0.4-0.69 doc mentions the topic but is not the actual evidence',
    '- 0.0-0.39 passing mention or unrelated',
  ].join('\n');
}

/**
 * Clamp + sanity-check the raw model output. Defensive: a model that
 * returns an out-of-range score, a too-long rationale, or extra
 * quotes shouldn't take down the pipeline — we coerce instead.
 */
function sanitiseScore(raw: {
  score?: unknown;
  rationale?: unknown;
  evidenceQuotes?: unknown;
  confidence?: unknown;
}): Omit<RelevanceScore, 'modelVersion' | 'cached'> {
  const score = Math.max(0, Math.min(1, Number(raw.score ?? 0)));
  const rationale = String(raw.rationale ?? '').slice(0, 200);
  const quotesRaw = Array.isArray(raw.evidenceQuotes) ? raw.evidenceQuotes : [];
  const evidenceQuotes = quotesRaw
    .map(q => String(q ?? '').slice(0, 240))
    .filter(q => q.length > 0)
    .slice(0, 3);
  const confidenceRaw = String(raw.confidence ?? 'low');
  const confidence: RelevanceConfidence =
    confidenceRaw === 'high' || confidenceRaw === 'medium' ? confidenceRaw : 'low';
  return { score, rationale, evidenceQuotes, confidence };
}

/**
 * A fresh score plus whether it is a verdict on the content. `transient`
 * marks the timeout / error / empty-body 0s: a later call can succeed,
 * so the caller must not cache them. The public transientFailure keeps
 * failure distinguishable from a content verdict; scoringModel records
 * which model produced a verdict.

 */
type FreshScore = Omit<RelevanceScore, 'modelVersion' | 'cached'> & {
  transient?: true;
  scoringModel?: string;
};

/**
 * Transport attempts per throttle slot on the proxy path — `runGeminiJSON`'s
 * default, stated here because each retry is counted as a request.
 */
const GEMINI_ATTEMPTS_PER_SLOT = 3;

/** The timeout non-verdict. Counts a timeout. */
function timeoutRow(timeoutMs: number): FreshScore {
  timeouts += 1;
  return {
    score: 0,
    rationale: `${TIMEOUT_RATIONALE_PREFIX}${timeoutMs}ms — treated as no match for safety.`,
    evidenceQuotes: [],
    confidence: 'low',
    transient: true,
    transientFailure: 'timeout',
  };
}

/** The error non-verdict. Counts an error. */
function errorRow(e: unknown): FreshScore {
  errors += 1;
  return {
    score: 0,
    rationale: `${ERROR_RATIONALE_PREFIX}${(e as Error)?.message ?? 'unknown'}) — treated as no match for safety.`,
    evidenceQuotes: [],
    confidence: 'low',
    transient: true,
    transientFailure: 'error',
  };
}

/**
 * Run a fresh Gemini call. Wrapped in `withTimeout` per SD1 so a hung
 * call returns a 0-score row instead of parking the caller. Transient provider
 * failures return a typed non-verdict. Caller cancellation
 * rejects with its authoritative reason, aborts active SDK/retries and cannot
 * turn abandoned work into an error/no-match row.
 */
async function callGeminiForScore(
  doc: SiteDocument,
  item: ChecklistItem,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<FreshScore> {
  // DMS-4 — filename plausibility short-circuit. If the doc's filename
  // clearly names a different doc kind from the requirement (e.g. a
  // `Data Center Scoring Sheet.xlsx` vs `flood-cert`), score 0 without
  // an LLM call. Saves cost AND prevents a fabricated cache row from
  // poisoning the per-`(doc, requirementId)` map that the DMS-2
  // strict path reads. Mirrors the synchronous gate inside
  // `docApplicability.ts` so DM2 + DAT agree.
  // P3 — a filename never vetoes a document whose CONTENT index names the
  // requirement ("notes.pdf" that is a wetlands delineation).
  if (filenameStronglyNegates(doc.fileName, item.id) && !documentContentNamesRequirement(doc, item.id)) {
    filenameShortCircuits += 1;
    return {
      score: 0,
      rationale: `Filename "${doc.fileName ?? '(unknown)'}" names a different document kind — refused without LLM call.`,
      evidenceQuotes: [],
      confidence: 'high',
      scoringModel: FILENAME_GATE_SCORER,
    };
  }
  if (documentBodyText(doc).trim().length === 0) {
    // No content — no possible match. Skip the call entirely. Transient:
    // the text can land (extraction / hydration) under the same
    // contentHash, so this 0 must not be cached.
    return {
      score: 0,
      rationale: NO_TEXT_RATIONALE,
      evidenceQuotes: [],
      confidence: 'high',
      transient: true,
    };
  }
  let runGeminiJSON: typeof import('../geminiClient').runGeminiJSON;
  let prompt: string;
  // D13 explicitly selected the configured Pro budget, independently of the
  // optional classification tiering flag; no thinking/recall cap is added.
  const model = GEMINI_PRO_MODEL;
  try {
    ({ runGeminiJSON } = await loadGeminiClient());
    prompt = buildScoringPrompt(doc, item);

  } catch (e) {
    rethrowIfAborted(signal, e);
    return errorRow(e);
  }
  const label = `doc-match:${doc.id}:${item.id}`;
  // On the proxy path a 429 is not re-fired inside runGeminiJSON (the IP
  // bucket stays closed for its Retry-After); every other transient keeps
  // the default retries, each counted as a request by the throttle.
  const throttled = isScoringThrottleEnabled();
  const retry = throttled
    ? {
        maxAttempts: GEMINI_ATTEMPTS_PER_SLOT,
        shouldRetry: (e: unknown) => !isRateLimitedError(e),
        onTransient: ({ attempt }: { attempt: number }) => {
          if (attempt < GEMINI_ATTEMPTS_PER_SLOT) noteScoringRetryRequest();
        },
      }
    : undefined;
  for (let attempt = 1; ; attempt += 1) {
    // Take the slot BEFORE the timeout starts: time spent waiting for the
    // proxy budget is not time the model took. Rejects only on abort.
    const release = await acquireScoringSlot(signal);
    // Caller cancellation and timeout both stop the SDK and its retries
    // before the throttle slot is released.
    const call = new AbortController();
    const onAbort = () => call.abort(signal?.reason);
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      throwIfAborted(signal);
      const raw = await withTimeout(
        raceWithAbort(runGeminiJSON<{
          score: number;
          rationale: string;
          evidenceQuotes: string[];
          confidence: RelevanceConfidence;
        }>({
          model,
          contents: prompt,
          schema: SCORING_SCHEMA as unknown as Parameters<typeof runGeminiJSON>[0]['schema'],
          timeoutMs,
          signal: call.signal,
          ...(retry ? { retry } : {}),
        }), signal),
        timeoutMs,
        label,
      );
      throwIfAborted(signal);
      return { ...sanitiseScore(raw), scoringModel: model };
    } catch (e) {
      rethrowIfAborted(signal, e);
      if (isTimeoutError(e)) {
        call.abort();
        return timeoutRow(timeoutMs);
      }
      if (throttled && isRateLimitedError(e)) {
        // Open the back-off window (the proxy's Retry-After), then try this
        // pair once more: the next slot is not granted before it passes.
        noteScoringRateLimited();
        if (attempt === 1) continue;
      }
      return errorRow(e);
    } finally {
      signal?.removeEventListener('abort', onAbort);
      if (signal?.aborted) call.abort(signal.reason);
      release();
    }
  }
}

/* ── Public API ──────────────────────────────────────────────────── */

/**
 * Score how strongly `doc` satisfies the evidence requirement of `item`.
 *
 * Strict-by-default — passing mentions return < 0.4 (no link), only docs
 * that ARE the requirement clear 0.7. Cached on Firestore so repeat
 * calls for the same `(docId, itemId, contentHash, MODEL_VERSION)` tuple
 * skip the Gemini call entirely.
 *
 * Unless deliberately cancelled, timeout / error returns a 0-score row with an
 * explanatory rationale so the caller's loop never trips. That row (and
 * the empty-body 0) is not cached, so a later call re-scores. The
 * timeout / error row carries `transientFailure`, so a caller that
 * caches results of its own can tell it from a "no match" verdict.
 * Caller cancellation rejects with its signal reason at cache, queue, SDK and
 * result boundaries; it is never cached or counted as a provider failure.
 */
export async function scoreDocAgainstChecklistItem(
  doc: SiteDocument,
  item: ChecklistItem,
  opts: ScoreDocOptions = {},
): Promise<RelevanceScore> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_SCORING_TIMEOUT_MS;
  throwIfAborted(opts.signal);
  // Docs without a contentHash predate the upload hook that stamps it —
  // we still score them, but cache under the fileName so back-compat
  // sites see cache hits on stable filenames. The hash IS the
  // invalidation lever in modern uploads; the filename fallback is a
  // last-resort identity that's at least stable across reruns.
  const contentHash = doc.contentHash ?? `nohash::${doc.fileName ?? doc.id}`;
  const key = cacheKeyFor({ docId: doc.id, itemId: item.id, contentHash });

  if (!opts.skipCache) {
    const cached = await raceWithAbort(readCachedScore(key), opts.signal);
    throwIfAborted(opts.signal);
    if (cached) {
      cacheHits += 1;
      return cached;
    }
  }
  cacheMisses += 1;

  const { transient, scoringModel, ...fresh } = await callGeminiForScore(doc, item, timeoutMs, opts.signal);
  throwIfAborted(opts.signal);

  const result: RelevanceScore = {
    ...fresh,
    modelVersion: MODEL_VERSION,
    cached: false,
  };

  // A timeout / error / empty-body 0 is not a verdict on this content:
  // hand it to the caller but leave the key unwritten so the next call
  // re-scores instead of reading a permanent "no match".
  if (transient) return result;

  // Await the cache write so the NEXT scoring call within the same
  // rebuild can see the row. `writeCachedScore` is already wrapped in
  // a try/catch so a Firestore outage can't propagate as a thrown
  // error here — awaiting is safe. Including identity fields so admin
  // tools can list rows without joining another table, and the TEL-04
  // write provenance so a row census can attribute each row.
  const provenance: DocMatchingWriteProvenance = {
    scoringModel: scoringModel ?? 'unknown',
    writerOrigin: docMatchingWriterOrigin(),
    buildSha: docMatchingWriterBuildSha(),
  };
  await raceWithAbort(writeCachedScore(key, {
    ...result,
    docId: doc.id,
    itemId: item.id,
    contentHash,
    siteId: opts.siteId ?? null,
    scoredAt: new Date().toISOString(),
    ...provenance,
  }), opts.signal);
  throwIfAborted(opts.signal);

  return result;
}
